import useRequest from "@/lib/request/useRequest";
import { getEnrollmentTierLabel } from "@/lib/utils/enrollment";
import { isNumber, roundTo } from "@/lib/utils/numbers";
import { getColumnValues } from "@/lib/utils/table";
import { OnDemandRxType } from "@/types/enums";
import {
    FieldWithPrecomputedRx,
    OptimalApproach,
    PrecomputedRx,
} from "@/types/rx";
import {
    faArrowDownShortWide,
    faArrowUpRightFromSquare,
    faArrowUpShortWide,
    faDownload,
    faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Checkbox,
    Chip,
    Divider,
    Input,
    Spinner,
} from "@heroui/react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    Row,
    useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/dashboard/home/bulk-rx")({
    component: RouteComponent,
});

type TableType = FieldWithPrecomputedRx & {
    rx_by_type: Record<string, PrecomputedRx[]>;
};

function RateCell({
    row,
    type,
    optimalApproach,
    selectedRxByFieldId,
    setSelectedRxByFieldId,
}: {
    row: Row<TableType>;
    type: OnDemandRxType;
    optimalApproach: OptimalApproach;
    selectedRxByFieldId: Record<number, number | undefined>;
    setSelectedRxByFieldId: (value: Record<number, number | undefined>) => void;
}) {
    const rateForTechnique = row.original.rx_by_type[type]?.find(
        (rx) => rx.optimal_approach == optimalApproach,
    );
    return (
        <div className="flex flex-col items-center gap-1">
            <Checkbox
                isDisabled={!rateForTechnique}
                isSelected={
                    rateForTechnique &&
                    selectedRxByFieldId[row.original.id] !== undefined &&
                    selectedRxByFieldId[row.original.id]?.toString() ==
                        rateForTechnique?.id.toString()
                }
                onValueChange={(value) => {
                    setSelectedRxByFieldId({
                        ...selectedRxByFieldId,
                        [row.original.id]: value
                            ? rateForTechnique?.id
                            : undefined,
                    });
                }}
            >
                {isNumber(rateForTechnique?.target_rate_n)
                    ? `${roundTo(rateForTechnique?.target_rate_n as number)}`
                    : "No Rate"}{" "}
            </Checkbox>

            {rateForTechnique && (
                <Button
                    size="sm"
                    color="primary"
                    as={Link}
                    target="_blank"
                    className="text-[0.6rem] p-0 h-[1.25rem]"
                    to={`/dashboard/fields/${row.original.id}/rx/${rateForTechnique?.rx}`}
                >
                    Go To Rx
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </Button>
            )}
        </div>
    );
}

function getColumnDef(
    label: string,
    type: OnDemandRxType,
    optimalApproach: OptimalApproach,
    selectedRxByFieldId: Record<number, number | undefined>,
    setSelectedRxByFieldId: (value: Record<number, number | undefined>) => void,
) {
    return {
        id: `${type}_${optimalApproach}`,
        header: label,
        accessorFn: (row: Row<TableType>) =>
            row.original?.rx_by_type?.[type]?.find(
                (rx) => rx.optimal_approach == optimalApproach,
            )?.target_rate_n ?? undefined,
        cell: ({ row }: { row: Row<TableType> }) => {
            return (
                <RateCell
                    row={row}
                    type={type}
                    optimalApproach={optimalApproach}
                    selectedRxByFieldId={selectedRxByFieldId}
                    setSelectedRxByFieldId={setSelectedRxByFieldId}
                />
            );
        },
    };
}

function RouteComponent() {
    const { useGetRequest, useMutateRequest } = useRequest();

    const { t } = useTranslation("messages");
    const ref = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(550);
    useEffect(() => {
        if (ref.current) {
            setHeight(ref.current.clientHeight);
        }
    }, []);

    const bulkRxQuery = useGetRequest<FieldWithPrecomputedRx[]>({
        url: "/precomputed_rx/by_field/",
    });

    const data = useMemo(() => {
        if (!bulkRxQuery.data) return [];
        return bulkRxQuery.data.map((field) => {
            return {
                ...field,
                rx_by_type: field.precomputed_rxs.reduce(
                    (acc, rx) => {
                        acc[rx.on_demand_type].push(rx);
                        return acc;
                    },
                    {
                        [OnDemandRxType.ShortTerm]: [],
                        [OnDemandRxType.TwoWeek]: [],
                        [OnDemandRxType.FourWeek]: [],
                        [OnDemandRxType.LastApplication]: [],
                    } as Record<OnDemandRxType, PrecomputedRx[]>,
                ),
            };
        });
    }, [bulkRxQuery.data]);

    const [selectedRxByFieldId, setSelectedRxByFieldId] = useState<
        Record<number, number | undefined>
    >({});

    const regenerateRxMutate = useMutateRequest({
        errorMessage: t("error.failed_to_regenerate"),
        successMessage: t("success.regenerated"),
    });

    const handleRegenerate = useCallback(
        (fieldId: number) => {
            regenerateRxMutate.mutate(
                {
                    url: `/fields/${fieldId}/precompute_rx/`,
                    method: "GET",
                },
                {
                    onSuccess: () => {
                        bulkRxQuery.refetch();
                    },
                },
            );
        },
        [bulkRxQuery, regenerateRxMutate],
    );

    const columns: ColumnDef<TableType>[] = useMemo(
        () => [
            {
                id: "account_manager",
                header: "Account manager",
                accessorFn: (row) =>
                    row.service_provider?.account_manager?.full_name ?? "N/A",
            },
            {
                id: "service_provider",
                header: "Service provider",
                accessorFn: (row) => row?.service_provider?.name ?? "",
            },
            {
                id: "farm",
                header: "Farm",
                accessorFn: (row) => row?.farm?.name ?? "",
            },
            {
                id: "field",
                header: "Field",
                accessorFn: (row) => row?.name ?? "",
                size: 120,
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-col items-start justify-between w-full">
                            <Link
                                to={`/dashboard/fields/$fieldId`}
                                params={{
                                    fieldId: row.original.id.toString(),
                                }}
                                className="text-primary-500"
                            >
                                {row.original.name}
                            </Link>
                            <Chip
                                color="primary"
                                radius="sm"
                                classNames={{
                                    base: "text-[0.6rem]",
                                    content: "p-[0.15rem]",
                                }}
                            >
                                {getEnrollmentTierLabel(
                                    row.original.active_enrollment
                                        ?.enrollment_tier,
                                )}
                            </Chip>
                            <Button
                                size="sm"
                                onPress={() =>
                                    handleRegenerate(row.original.id)
                                }
                                isLoading={regenerateRxMutate.isPending}
                                isDisabled={regenerateRxMutate.isPending}
                            >
                                Generate
                            </Button>
                        </div>
                    );
                },
            },
            {
                id: "quick_info",
                header: "Product",
                accessorFn: (row) => {
                    return row?.default_product?.name ?? "";
                },
                cell: ({ row }) => {
                    // Get the latest precomputed_rx by created_date
                    const precomputedRxs = row.original.precomputed_rxs;
                    const latestRx =
                        Array.isArray(precomputedRxs) &&
                        precomputedRxs.length > 0
                            ? precomputedRxs.reduce((latest, current) => {
                                  return new Date(current.created_date) >
                                      new Date(latest.created_date)
                                      ? current
                                      : latest;
                              }, precomputedRxs[0])
                            : undefined;

                    return (
                        <div className="text-xs space-y-0.5">
                            <div>
                                <span className="font-semibold">Product: </span>
                                <span>
                                    {row.original.default_product?.name ?? "-"}
                                </span>
                            </div>
                            <div>
                                <span className="font-semibold">
                                    Planting:{" "}
                                </span>
                                <span>{latestRx?.planting_date ?? "-"}</span>
                            </div>
                            <div>
                                <span className="font-semibold">
                                    N Applied:{" "}
                                </span>
                                <span>
                                    {latestRx?.n_applied_lbs !== undefined
                                        ? `${roundTo(latestRx.n_applied_lbs)} lbs`
                                        : "-"}
                                </span>
                            </div>
                            <div>
                                <span className="font-semibold">Irrig: </span>
                                <span>
                                    {latestRx?.irrigation_amount_inches !==
                                    undefined
                                        ? `${roundTo(latestRx.irrigation_amount_inches)} in`
                                        : "-"}
                                </span>
                            </div>
                        </div>
                    );
                },
            },
            getColumnDef(
                "Short Term (Imagery Only)",
                OnDemandRxType.ShortTerm,
                OptimalApproach.IMAGERY_ONLY,
                selectedRxByFieldId,
                setSelectedRxByFieldId,
            ),
            getColumnDef(
                "4 WK - 2%",
                OnDemandRxType.FourWeek,
                OptimalApproach.N_UPTAKE_2_PERC_THRESHOLD,
                selectedRxByFieldId,
                setSelectedRxByFieldId,
            ),
            getColumnDef(
                "4 WK - 3%",
                OnDemandRxType.FourWeek,
                OptimalApproach.N_UPTAKE_3_PERC_THRESHOLD,
                selectedRxByFieldId,
                setSelectedRxByFieldId,
            ),
            getColumnDef(
                "4 WK - 4%",
                OnDemandRxType.FourWeek,
                OptimalApproach.N_UPTAKE_4_PERC_THRESHOLD,
                selectedRxByFieldId,
                setSelectedRxByFieldId,
            ),
            getColumnDef(
                "4 WK - Profit",
                OnDemandRxType.FourWeek,
                OptimalApproach.OPTIMAL_PROFIT,
                selectedRxByFieldId,
                setSelectedRxByFieldId,
            ),
            getColumnDef(
                "Last - 2%",
                OnDemandRxType.LastApplication,
                OptimalApproach.N_UPTAKE_2_PERC_THRESHOLD,
                selectedRxByFieldId,
                setSelectedRxByFieldId,
            ),
            getColumnDef(
                "Last - 3%",
                OnDemandRxType.LastApplication,
                OptimalApproach.N_UPTAKE_3_PERC_THRESHOLD,
                selectedRxByFieldId,
                setSelectedRxByFieldId,
            ),
            getColumnDef(
                "Last - 4%",
                OnDemandRxType.LastApplication,
                OptimalApproach.N_UPTAKE_4_PERC_THRESHOLD,
                selectedRxByFieldId,
                setSelectedRxByFieldId,
            ),
            getColumnDef(
                "Last - Profit",
                OnDemandRxType.LastApplication,
                OptimalApproach.OPTIMAL_PROFIT,
                selectedRxByFieldId,
                setSelectedRxByFieldId,
            ),
        ],
        [handleRegenerate, regenerateRxMutate.isPending, selectedRxByFieldId],
    );

    const [globalSearch, setGlobalSearch] = useState("");
    const [globalFilter, setGlobalFilter] = useState("");

    useEffect(() => {
        const debouncedUpdate = debounce(() => {
            setGlobalFilter(globalSearch);
        }, 200);

        debouncedUpdate();

        return () => debouncedUpdate.cancel();
    }, [globalSearch]);

    const table = useReactTable<TableType>({
        columns,
        data: data,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            globalFilter,
        },
        initialState: {
            sorting: [{ id: "field", desc: false }],
        },
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            return String(value)
                .toLowerCase()
                .includes(filterValue.toLowerCase());
        },
    });

    const { rows: prefilteredRows } = table.getPreFilteredRowModel();

    const filterOptions = {
        account_manager: getColumnValues(
            "account_manager",
            prefilteredRows,
        ).map((account_manager) => {
            return (
                <AutocompleteItem
                    key={account_manager}
                    textValue={account_manager}
                >
                    {account_manager}
                </AutocompleteItem>
            );
        }),

        service_provider: getColumnValues(
            "service_provider",
            prefilteredRows,
        ).map((service_provider) => {
            return (
                <AutocompleteItem
                    key={service_provider}
                    textValue={
                        service_provider != ""
                            ? service_provider
                            : "no service provider"
                    }
                >
                    {service_provider}
                </AutocompleteItem>
            );
        }),

        farm: getColumnValues("farm", prefilteredRows).map((farm) => {
            return (
                <AutocompleteItem
                    key={farm}
                    textValue={farm != "" ? farm : "no farm"}
                >
                    {farm}
                </AutocompleteItem>
            );
        }),
    };

    const downloadMutate = useMutateRequest<Blob>({
        errorMessage: t("error.failed_to_export"),
    });

    const handleExport = useCallback(() => {
        if (
            !Object.values(selectedRxByFieldId).filter((id) => id !== undefined)
                .length
        ) {
            return;
        }
        downloadMutate.mutate(
            {
                url: "/precomputed_rx/export/",
                method: "POST",
                data: {
                    precomputed_rx_ids: Object.values(
                        selectedRxByFieldId,
                    ).filter((id) => id !== undefined),
                },
                responseType: "blob",
            },
            {
                onSuccess: (response) => {
                    const blob = new Blob([response], {
                        type: "application/zip",
                    });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    const now = new Date();
                    const pad = (n: number) => n.toString().padStart(2, "0");
                    const datetime = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
                    const rxCount = Object.values(selectedRxByFieldId).filter(
                        (id) => id !== undefined,
                    ).length;
                    a.download = `bulk_rx_${datetime}_${rxCount}_rxs.zip`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                },
            },
        );
    }, [downloadMutate, selectedRxByFieldId]);

    const { rows } = table.getRowModel();

    const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
        count: rows.length,
        estimateSize: () => 33, //estimate row height for accurate scrollbar dragging
        getScrollElement: () => ref.current,
        //measure dynamic row height, except in firefox because it measures table border height incorrectly
        measureElement:
            typeof window !== "undefined" &&
            navigator.userAgent.indexOf("Firefox") === -1
                ? (element) => element?.getBoundingClientRect().height
                : undefined,
        overscan: 5,
    });

    if (bulkRxQuery.isLoading) {
        return <Spinner label="Loading..." />;
    }

    return (
        <div className="h-full flex flex-col bg-white">
            <div className="flex flex-row gap-2 p-2 items-center justify-between">
                <div>
                    <Input
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                        value={globalSearch}
                        isClearable
                        onClear={() => setGlobalSearch("")}
                        onChange={(e) =>
                            setGlobalSearch(String(e.target.value))
                        }
                        placeholder="Search..."
                    />
                </div>
                <div className="flex flex-row gap-2 items-center">
                    <Button
                        startContent={<FontAwesomeIcon icon={faDownload} />}
                        onPress={handleExport}
                        isLoading={downloadMutate.isPending}
                        isDisabled={
                            downloadMutate.isPending ||
                            !Object.values(selectedRxByFieldId).filter(
                                (id) => id !== undefined,
                            ).length
                        }
                    >
                        Export
                    </Button>
                </div>
            </div>
            <Divider />
            <div ref={ref} className="h-[100%] overflow-y-scroll  bg-white">
                <table style={{ display: "grid", height: height }}>
                    <thead
                        style={{
                            display: "grid",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                        }}
                        className="bg-white"
                    >
                        {table.getHeaderGroups().map((group) => (
                            <tr className="border-b py-[0.25rem] border-gray-700">
                                {group.headers.map((header) => {
                                    return (
                                        <th
                                            aria-label={
                                                header.column.columnDef.header?.toString() ??
                                                "column header"
                                            }
                                            key={header.id}
                                            style={{
                                                width: header.column.getSize(),
                                            }}
                                            className="pointer-events-none"
                                        >
                                            <div
                                                onClick={header.column.getToggleSortingHandler()}
                                                style={{
                                                    width: header.column.getSize(),
                                                }}
                                                className={classNames({
                                                    "hover:bg-gray-200 rounded-lg p-2 text-medium pointer-events-auto mb-1":
                                                        true,
                                                    "cursor-pointer":
                                                        header.column.getCanSort(),
                                                })}
                                            >
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                                {header.column.getCanSort() &&
                                                    header.column.getIsSorted() && (
                                                        <FontAwesomeIcon
                                                            className="ml-2 text-primary-500"
                                                            icon={
                                                                header.column.getIsSorted() ===
                                                                "asc"
                                                                    ? faArrowUpShortWide
                                                                    : faArrowDownShortWide
                                                            }
                                                            size="sm"
                                                        />
                                                    )}
                                            </div>

                                            {header.column.getCanFilter() &&
                                                header.column.id in
                                                    (filterOptions ?? {}) && (
                                                    <div tabIndex={-1}>
                                                        <Autocomplete
                                                            color={
                                                                header.column.getFilterValue()
                                                                    ? "primary"
                                                                    : "default"
                                                            }
                                                            aria-label="Filter"
                                                            className="w-full pointer-events-auto"
                                                            size="sm"
                                                            showScrollIndicators={
                                                                false
                                                            }
                                                            selectedKey={
                                                                header.column.getFilterValue() as string
                                                            }
                                                            onSelectionChange={(
                                                                e,
                                                            ) => {
                                                                header.column.setFilterValue(
                                                                    e,
                                                                );
                                                            }}
                                                        >
                                                            {
                                                                (filterOptions ??
                                                                    {})[
                                                                    header
                                                                        .column
                                                                        .id as keyof typeof filterOptions
                                                                ]
                                                            }
                                                        </Autocomplete>
                                                    </div>
                                                )}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody
                        style={{
                            display: "grid",
                            height: `${rowVirtualizer.getTotalSize()}px`, //tells scrollbar how big the table is
                            position: "relative", //needed for absolute positioning of rows
                        }}
                    >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                            const row = rows[
                                virtualRow.index
                            ] as Row<TableType>;
                            return (
                                <tr
                                    data-index={virtualRow.index} //needed for dynamic row height measurement
                                    ref={(node) =>
                                        rowVirtualizer.measureElement(node)
                                    } //measure dynamic row height
                                    key={row.id}
                                    style={{
                                        display: "flex",
                                        position: "absolute",
                                        transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
                                        width: "100%",
                                    }}
                                    className="border-b py-[0.25rem] border-gray-700"
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <td
                                                key={cell.id}
                                                style={{
                                                    display: "flex",
                                                    width: cell.column.getSize(),
                                                }}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
